import type React from 'react';
import { useRef } from 'react';

interface ShimmerProps {
    primaryColor?: string;
    className?: string;
    variant?: 'default' | 'border' | 'both' | 'diagonal';
    animationSpeed?: number; // Speed of the animation in seconds (e.g., 2)
    animationPause?: number; // Pause between animations in seconds (e.g., 3)
    easing?: string; // Animation easing (e.g., 'ease-in-out')
    oneTime?: boolean; // If true, animation runs only once instead of infinite
}

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number): string => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Parse hex values
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Shimmer: React.FC<ShimmerProps> = ({
    primaryColor = '#000',
    className = '',
    variant = 'both',
    animationSpeed = 2, // Default 2 seconds for animation
    animationPause = 3, // Default 3 seconds pause
    easing = 'ease-in-out',
    oneTime = false,
}) => {
    // Convert primary color to rgba variants for the gradient
    const colorLight = hexToRgba(primaryColor, 0.25); // 40 in hex ≈ 0.25 opacity
    const colorMedium = hexToRgba(primaryColor, 0.5); // 80 in hex ≈ 0.5 opacity

    // Calculate total duration and percentage for animation vs pause
    const totalDuration = animationSpeed + animationPause;
    const animationPercentage = (animationSpeed / totalDuration) * 100;

    // Generate unique animation names to avoid conflicts - cached to prevent style thrash
    const idRef = useRef(`shimmer-${Math.random().toString(36).substring(2, 11)}`);
    const uniqueId = idRef.current;
    const slideAnimation = `${uniqueId}-slide`;
    const pulseAnimation = `${uniqueId}-pulse`;
    const diagonalAnimation = `${uniqueId}-diagonal`;

    // Determine iteration count
    const iterationCount = oneTime ? '1' : 'infinite';

    return (
        <>
            {/* CSS Animations defined inline for component isolation */}
            <style>{`
                @keyframes ${slideAnimation} {
                    0% {
                        transform: translateX(-100%);
                    }
                    ${animationPercentage}% {
                        transform: translateX(200%);
                    }
                    100% {
                        transform: translateX(200%);
                    }
                }

                @keyframes ${pulseAnimation} {
                    0% {
                        opacity: 0.5;
                    }
                    ${animationPercentage / 2}% {
                        opacity: 0.8;
                    }
                    ${animationPercentage}% {
                        opacity: 0.5;
                    }
                    100% {
                        opacity: 0.5;
                    }
                }

                @keyframes ${diagonalAnimation} {
                    0% {
                        transform: translateX(-100%) translateY(-100%);
                        opacity: 0;
                    }
                    20% {
                        opacity: 1;
                    }
                    80% {
                        opacity: 1;
                    }
                    100% {
                        transform: translateX(50%) translateY(50%);
                        opacity: 0;
                    }
                }

                .shimmer-gradient {
                    background: linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255, 255, 255, 0) 40%,
                        rgba(255, 255, 255, 0.6) 50%,
                        rgba(255, 255, 255, 0) 60%,
                        transparent 100%
                    );
                }
            `}</style>
            {/* Inner shimmer - light sweep effect */}
            {(variant === 'default' || variant === 'both') && (
                <div
                    className={`absolute inset-0 shimmer-gradient pointer-events-none z-10 ${className}`}
                    style={{
                        animationName: slideAnimation,
                        animationDuration: `${totalDuration}s`,
                        animationTimingFunction: easing,
                        animationIterationCount: iterationCount,
                        animationFillMode: 'both',
                    }}
                />
            )}

            {/* Border shimmer - colored border effect */}
            {(variant === 'border' || variant === 'both') && (
                <div
                    className={`absolute -inset-0.5 rounded-xl pointer-events-none -z-10 opacity-80 ${className}`}
                    style={{
                        background: `linear-gradient(90deg, transparent, ${colorLight}, ${colorMedium}, ${colorLight}, transparent)`,
                        backgroundSize: '200% 100%',
                        animationName: pulseAnimation,
                        animationDuration: `${totalDuration}s`,
                        animationTimingFunction: easing,
                        animationIterationCount: iterationCount,
                        animationFillMode: 'both',
                    }}
                />
            )}

            {/* Diagonal shimmer - button-style effect */}
            {variant === 'diagonal' && (
                <div
                    className={`absolute inset-0 pointer-events-none ${className}`}
                    style={{
                        background:
                            'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 80%)',
                        width: '200%',
                        height: '200%',
                        animationName: diagonalAnimation,
                        animationDuration: `${animationSpeed}s`,
                        animationTimingFunction: easing,
                        animationIterationCount: iterationCount,
                        animationFillMode: 'forwards',
                    }}
                />
            )}
        </>
    );
};

export default Shimmer;
